import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { captureCalendarDeepLink } from './calendarDeepLink';

/**
 * Erases the calendar deep link's `?d=`/`?e=` from the address bar on arrival,
 * stashing them in memory for EventList to read instead.
 *
 * THE-787. Mounted at the App root and — this is the whole point — declared
 * BEFORE `usePageTracking()` in the same component, so its effect runs first
 * and the holiday identifier is out of `document.location.href` before
 * `fbq('track','PageView')` reads it for Meta's automatic `dl`. See
 * `captureCalendarDeepLink` for why no change inside the lazy-loaded calendar
 * can do this, and `deepLinkArrivalPageView.test.tsx` for the ordering pinned
 * against a real render.
 *
 * Lives in `calendar/` rather than `hooks/` on purpose: the calendar owns these
 * param names and their id shapes, so the module that mints a deep link, the
 * one that erases it and the one that reads it stay in one place. App.js owns
 * only the ordering.
 *
 * Keyed on pathname AND search, matching `usePageTracking`'s own deps, so the
 * capture runs on every commit that hook could fire a PageView on — including a
 * same-pathname `/calendar` → `/calendar?d=&e=` navigation. `captureCalendarDeepLink`
 * is a no-op on any URL with nothing to erase, and is scoped to `/calendar`.
 *
 * Note it reads the LIVE `window.location`, not this `location`: react-router
 * has already written the history entry by the time an effect runs, and the
 * live URL is the one fbevents.js reads. `location` here is the trigger, not
 * the source.
 */
export function useCalendarDeepLinkCapture(): void {
  const location = useLocation();
  useEffect(() => {
    captureCalendarDeepLink(location.pathname);
  }, [location.pathname, location.search]);
}
